import {
  Edit,
  useEditContext,
  useNotify,
  useRedirect,
  usePermissions
} from "react-admin";


import { useEffect, useState } from "react";
import {
  Button,
  TextField,
  Tabs,
  Tab,
  Box,
  MenuItem,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import IconButton from "@mui/material/IconButton";

import { apiUrl } from "../apiendpoint";



class UploadAdapter {
  constructor(loader, uploadUrl) {
    this.loader = loader;
    this.uploadUrl = uploadUrl;
  }

  upload() {
    return this.loader.file.then(file =>
      new Promise((resolve, reject) => {
        const data = new FormData();
        data.append("file", file);
        data.append("question_id", this.questionId);

        fetch(`${apiUrl}uploadUrls`, {
          method: "POST",
          body: data,
          headers:{
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          }
        })
          .then(res => res.json())
          .then(res => resolve({ default: res.url }))
          .catch(err => reject(err));
      })
    );
  }

  abort() {}
}

const RichTextInput = ({ value, onChange, label }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <div style={{ marginTop: 10 }}>
      <label>{label}</label>

      <div style={{ borderRadius: "4px", minHeight: "150px", width: "800px" }}>
        <CKEditor
          editor={ClassicEditor}
          data={value || ""}
          onReady={(editor) => {
            editor.ui.view.editable.element.style.color = isDark ? "#fff" : "#000";
            editor.ui.view.editable.element.style.padding = "10px";
            editor.ui.view.editable.element.style.minHeight = "150px";

            editor.plugins.get("FileRepository").createUploadAdapter = (loader) =>
              new UploadAdapter(loader, `${apiUrl}uploads`);
          }}
          config={{
            placeholder: "Type your question here...",
            toolbar: [
              "heading",
              "|",
              "bold",
              "italic",
              "link",
              "bulletedList",
              "numberedList",
              "blockQuote",
              "imageUpload",
              "undo",
              "redo",
              "insertTable",
            ],
          }}
          onChange={(event, editor) => onChange(editor.getData())}
        />
      </div>
    </div>
  );
};

const QuestionEditInner = () => {
  const { record, isLoading } = useEditContext();
  const notify = useNotify();
  const redirect = useRedirect();

  const [tab, setTab] = useState(0);
  const [categories, setCategories] = useState([]);
  const [categoryBlocks, setCategoryBlocks] = useState([]);
  const [question, setQuestion] = useState(null);

  /* ================= FETCH CATEGORIES ================= */
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    fetch(`${apiUrl}categories`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then(res => res.json())
      .then(data => setCategories(data || []))
      .catch(console.error);
  }, []);

  /* ================= PREFILL ================= */
  useEffect(() => {
    if (!record || categories.length === 0) return;

    // CATEGORY PREFILL
    const blocks = (record.assessment_category_ids || []).map(id => {
      const sub = categories.find(c => Number(c.id) === Number(id));
      return sub
        ? {
            category: sub.category_name,
            subcategory: String(sub.id),
          }
        : { category: "", subcategory: "" };
    });

    setCategoryBlocks(
      blocks.length ? blocks : [{ category: "", subcategory: "" }]
    );

    // QUESTION PREFILL
    const options = record.question_data?.options || [];
    const correctText = record.question_data?.answer?.correct_option;

    setQuestion({
      title: record.question_data?.question_text || "",
      marks: record.question_data?.marks || 0,
      difficulty: record.difficulty_level || "easy",
      options: options.map((o, i) => ({ id: i, text: o })),
      correct: correctText || "",
    });

  }, [record, categories]);

  if (isLoading || !question) return <div>Loading...</div>;

  /* ================= GROUP ================= */
  const grouped = categories.reduce((acc, item) => {
    if (!acc[item.category_name]) acc[item.category_name] = [];
    acc[item.category_name].push(item);
    return acc;
  }, {});

  /* ================= HANDLERS ================= */
  const updateField = (field, value) => {
    setQuestion(prev => ({ ...prev, [field]: value }));
  };

  const updateOption = (index, value) => {
    const updated = [...question.options];
    updated[index].text = value;
    setQuestion(prev => ({ ...prev, options: updated }));
  };

  const addOption = () => {
    setQuestion(prev => ({
      ...prev,
      options: [...prev.options, { id: Date.now(), text: "" }],
    }));
  };

  /* ================= SUBMIT ================= */

const handleSubmit = async () => {
  try {
    const token = localStorage.getItem("access_token");
    const payload = {
      title: question.title,
      assessment_category_ids: categoryBlocks
        .map((b) => Number(b.subcategory))
        .filter(Boolean),
      difficulty_level: question.difficulty,
      question_data: {
        type: "mcq",
        question_text: question.title,
        marks: question.marks,
        options: question.options.map((o) => o.text),
        answer: {
          correct_option: question.correct,
        },
      },
    };
    

const res = await fetch(`${apiUrl}questions/${record.id}`,
		 {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error("Failed to create question");
    notify("✅ Question created successfully", {
      type: "success",
    });
    redirect("/questions");
  } catch (err) {
    console.error(err);
    notify("❌ Create failed", {
      type: "error",
    });
  }
};

return (
  <Box sx={{ maxWidth: "900px" }}>

    {/* TABS HEADER */}
    <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        textColor="primary"
        indicatorColor="primary"
      >
        <Tab label="CATEGORY" />
        <Tab label="QUESTION" />
      </Tabs>
    </Box>

    {/* ================= CATEGORY TAB ================= */}
    {tab === 0 && (
      <Box>
        <h1>Select Categories</h1>

        {categoryBlocks.map((block, i) => (
  <Box
    key={i}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 2,
      mb: 2,
      flexWrap: "wrap",
    }}
  >
    {/* CATEGORY */}
    <select
      value={block.category}
      onChange={(e) => {
        const updated = [...categoryBlocks];

        updated[i] = {
          category: e.target.value,
          subcategory: "",
        };

        setCategoryBlocks(updated);
      }}
      style={{
        padding: "10px 14px",
        minWidth: "260px",
        height: "52px",
        borderRadius: "8px",
        border: "1px solid #ccc",
        fontSize: "16px",
        background: "#fff",
      }}
    >
      <option value="">Select Category</option>

      {Object.keys(grouped).map((cat) => (
        <option key={cat} value={cat}>
          {cat}
        </option>
      ))}
    </select>

    {/* SUBCATEGORY */}
    <select
      value={block.subcategory}
      onChange={(e) => {
        const updated = [...categoryBlocks];

        updated[i].subcategory = e.target.value;

        setCategoryBlocks(updated);
      }}
      style={{
        padding: "10px 14px",
        minWidth: "260px",
        height: "52px",
        borderRadius: "8px",
        border: "1px solid #ccc",
        fontSize: "16px",
        background: "#fff",
      }}
    >
      <option value="">Select Subcategory</option>

      {(grouped[block.category] || []).map((sub) => (
        <option key={sub.id} value={sub.id}>
          {sub.subcategory_name}
        </option>
      ))}
    </select>

    {/* ADD BUTTON ONLY LAST ROW */}
    {i === categoryBlocks.length - 1 && (
      <IconButton
        color="primary"
        onClick={() =>
          setCategoryBlocks([
            ...categoryBlocks,
            {
              category: "",
              subcategory: "",
            },
          ])
        }
      >
        <AddCircleOutlineIcon />
      </IconButton>
    )}

    {/* DELETE BUTTON */}
    {categoryBlocks.length > 1 && (
      <IconButton
        color="error"
        onClick={() => {
          const updated = categoryBlocks.filter(
            (_, index) => index !== i
          );

          setCategoryBlocks(updated);
        }}
      >
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    )}
  </Box>
))}
        <Button
          variant="contained"
          sx={{ mt: 2 }}
          onClick={() => setTab(1)}
        >
          Next → Questions
        </Button>
      </Box>
    )}

    {/* ================= QUESTION TAB ================= */}
    {tab === 1 && (
      <Box>
        <Button onClick={() => setTab(0)} sx={{ mb: 2 }}>
          ← Back to Category
        </Button>

        {/* QUESTION CARD */}
        <Box
          sx={{
            p: 3,
            border: "1px solid #333",
            borderRadius: "10px",
            mb: 3,
          }}
        >
          <h2>Edit Question</h2>

          <RichTextInput
            label="Question"
            value={question.title}
            onChange={(val) => updateField("title", val)}
          />
          {/* MARKS + DIFFICULTY STACK */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              maxWidth: "400px",
            }}
          >
            <TextField
              label="Marks"
              type="number"
              value={question.marks}
              fullWidth
              onChange={(e) =>
                updateField("marks", Number(e.target.value))
              }
              sx={{
                "& .MuiInputBase-root": {
                  height: "60px",
                },
              }}
            />

            <TextField
              select
              label="Difficulty"
              value={question.difficulty}
              fullWidth
              onChange={(e) =>
                updateField("difficulty", e.target.value)
              }
              sx={{
                "& .MuiInputBase-root": {
                  height: "60px",
                },
              }}
            >
              <MenuItem value="easy">Easy</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="hard">Hard</MenuItem>
            </TextField>
          </Box>

          {/* OPTIONS */}
          <h3 style={{ marginTop: 20 }}>Options</h3>

          {question.options.map((opt, index) => (
            <Box
              key={opt.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 1,
              }}
            >
              <TextField
                label={`Option ${index + 1}`}
                value={opt.text}
                fullWidth
                onChange={(e) =>
                  updateOption(index, e.target.value)
                }
              />

              <input
                type="radio"
                checked={question.correct === opt.text}
                onChange={() =>
                  updateField("correct", opt.text)
                }
              />
              <span>Correct</span>
            </Box>
          ))}

          <Button onClick={addOption}>ADD OPTION</Button>
        </Box>

        <Button variant="contained" onClick={handleSubmit}>
          UPDATE QUESTION
        </Button>
      </Box>
    )}
  </Box>
);
};




/* UploadAdapter, RichTextInput etc remain same */


/*  MOVE THIS TO BOTTOM */
const QuestionEdit = (props) => {
  const { permissions = [] } = usePermissions();

  if (
    !(
      permissions.includes("assessments.edit") ||
      permissions.includes("assessments.*") ||
      permissions.includes("*")
    )
  ) {
    return null;
  }

  return (
    <Edit {...props}>
      <QuestionEditInner />
    </Edit>
  );
};

export default QuestionEdit;